import React, { useState, useEffect, useCallback, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import { useTranslation } from "react-i18next";
import { useBrand } from "../context/BrandContext";
import { getMyClubs } from "./funtionsBD/Mainservice";
import { resolveClubImageUrl } from "../utils/resolveClubImageUrl";
import { executeClubSwitch, readStoredUserSafe } from "../utils/switchActiveClubClient";
import "./SwitchClubDialog.css";

function clubInitial(name) {
  const letter = String(name || "?").trim().charAt(0);
  return letter ? letter.toUpperCase() : "?";
}

export default function SwitchClubDialog() {
  const { t } = useTranslation();
  const { refreshBrand } = useBrand();
  const [open, setOpen] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [switchingId, setSwitchingId] = useState(null);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const activeClubId = useMemo(() => {
    const u = readStoredUserSafe();
    const stored = String(
      u.club_id || u.club_details?.club_id || u.club?.club_id || ""
    ).trim();
    if (stored && clubs.some((c) => String(c.club_id) === stored)) {
      return stored;
    }
    const active = clubs.find((c) => c.is_active);
    if (active?.club_id) return String(active.club_id);
    return stored;
  }, [clubs]);

  const fetchClubs = useCallback(async () => {
    const res = await getMyClubs();
    const list = res?.data?.data?.clubs || res?.data?.clubs || [];
    return Array.isArray(list) ? list : [];
  }, []);

  useEffect(() => {
    const handleOpen = async () => {
      setError("");
      setSuccessMsg("");
      try {
        const list = await fetchClubs();
        setClubs(list);
        if (list.length > 1) {
          setOpen(true);
        }
      } catch (err) {
        console.warn("SwitchClubDialog: getMyClubs error:", err?.message || err);
        setClubs([]);
      }
    };

    window.addEventListener("smarttt:open-club-switcher", handleOpen);
    window.addEventListener("smarttt-open-club-switcher", handleOpen);
    return () => {
      window.removeEventListener("smarttt:open-club-switcher", handleOpen);
      window.removeEventListener("smarttt-open-club-switcher", handleOpen);
    };
  }, [fetchClubs]);

  const activeClub = clubs.find((c) => String(c.club_id || c.id || "") === activeClubId);

  const handleClose = () => {
    if (switchingId) return;
    setOpen(false);
    setError("");
    setSuccessMsg("");
  };

  const handleSwitch = async (targetClub) => {
    const targetId = String(targetClub?.club_id || targetClub?.id || "").trim();
    if (!targetId || targetId === activeClubId || switchingId) return;

    try {
      setSwitchingId(targetId);
      setError("");
      await executeClubSwitch(targetId, refreshBrand);
      setSuccessMsg(
        t("switchedToClub", "Switched to {{name}}. Active branding and data updated.", {
          name: targetClub?.club_name || "Club",
        })
      );
      setClubs((prev) =>
        prev.map((c) => ({
          ...c,
          is_active: String(c.club_id) === targetId,
        }))
      );
      setTimeout(() => {
        setOpen(false);
        setSwitchingId(null);
        setSuccessMsg("");
      }, 700);
    } catch (err) {
      console.error("Failed to switch club:", err);
      setError(err?.response?.data?.message || err?.message || t("clubSwitchFailed", "Could not switch club"));
      setSwitchingId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      className="switch-club-dialog"
      sx={{ zIndex: 13000 }}
      aria-labelledby="switch-club-title"
      aria-describedby="switch-club-prompt"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "color-mix(in srgb, var(--bg-color, #0b0f19) 46%, transparent)",
            backdropFilter: "blur(18px) saturate(1.15)",
            WebkitBackdropFilter: "blur(18px) saturate(1.15)",
          },
        },
        paper: { className: "switch-club-dialog__paper" },
      }}
    >
      <div className="switch-club-dialog__wash" aria-hidden="true" />
      <header className="switch-club-dialog__header">
        <span className="switch-club-dialog__mark" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 7h11M15 4l3 3-3 3" />
            <path d="M17 17H6M9 14l-3 3 3 3" />
          </svg>
        </span>
        <div className="switch-club-dialog__heading">
          <p className="switch-club-dialog__eyebrow">{t("switchClubTitle", "Switch Club")}</p>
          <h2 className="switch-club-dialog__title" id="switch-club-title">
            {activeClub?.club_name || t("switchClubTitle", "Switch Club")}
          </h2>
          <p className="switch-club-dialog__prompt" id="switch-club-prompt">
            {t("switchClubPrompt", "Do you want to switch to another club?")}
          </p>
        </div>
        <button
          type="button"
          className="switch-club-dialog__close"
          onClick={handleClose}
          disabled={Boolean(switchingId)}
          aria-label={t("close", "Close")}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>

      <div className="switch-club-dialog__body">
        {error ? (
          <p className="switch-club-dialog__banner switch-club-dialog__banner--error" role="alert">
            {error}
          </p>
        ) : null}
        {successMsg ? (
          <p className="switch-club-dialog__banner switch-club-dialog__banner--success" role="status">
            {successMsg}
          </p>
        ) : null}

        {clubs.length === 0 ? (
          <div className="switch-club-dialog__empty">
            <p>{t("noOtherClubsFound", "No accessible clubs found.")}</p>
          </div>
        ) : (
          <ul className="switch-club-dialog__list">
            {clubs.map((c) => {
              const cid = String(c.club_id || c.id || "");
              const isActive = cid === activeClubId;
              const isSwitchingThis = switchingId === cid;
              const logo =
                resolveClubImageUrl(c.logo_url) ||
                resolveClubImageUrl(c.club_image) ||
                null;
              const name = c.club_name || "";
              const pills = [
                c.country,
                c.is_admin ? t("admin", "Admin") : null,
                c.is_coach ? t("coach", "Coach") : null,
              ].filter(Boolean);

              const body = (
                <>
                  {logo ? (
                    <img className="switch-club-dialog__logo" src={logo} alt="" />
                  ) : (
                    <span className="switch-club-dialog__initial" aria-hidden="true">
                      {clubInitial(name)}
                    </span>
                  )}
                  <span className="switch-club-dialog__meta">
                    <span className="switch-club-dialog__name">{name}</span>
                    {pills.length > 0 ? (
                      <span className="switch-club-dialog__pills">
                        {pills.map((label, index) => (
                          <span className="switch-club-dialog__pill" key={`${label}-${index}`}>
                            {label}
                          </span>
                        ))}
                      </span>
                    ) : null}
                  </span>
                  <span
                    className={
                      isActive
                        ? "switch-club-dialog__action switch-club-dialog__action--active"
                        : "switch-club-dialog__action switch-club-dialog__action--select"
                    }
                  >
                    {isSwitchingThis ? (
                      <span className="switch-club-dialog__spinner" aria-hidden="true" />
                    ) : isActive ? (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M5 12.5l4.2 4.2L19 7.5" />
                        </svg>
                        {t("activeClub", "Active")}
                      </>
                    ) : (
                      t("select", "Select")
                    )}
                  </span>
                </>
              );

              return (
                <li key={cid}>
                  {isActive ? (
                    <div className="switch-club-dialog__club-static" aria-current="true">
                      {body}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="switch-club-dialog__club"
                      onClick={() => handleSwitch(c)}
                      disabled={Boolean(switchingId)}
                    >
                      {body}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
